import React, { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { installGenesisInteractions } from './genesisInteractions';

// The model owns layout, never application privileges. Only this fixed bridge runs.
export function GenesisHost({ html, onGenesisAction }) {
  const frame = useRef(null);
  const [height, setHeight] = useState(240);
  const nonce = useMemo(
    () => Array.from(crypto.getRandomValues(new Uint32Array(4))).join(''),
    []
  );
  const documentHtml = useMemo(() => {
    const safe = DOMPurify.sanitize(html || '', {
      ADD_TAGS: ['style'],
      ADD_ATTR: [
        'data-action',
        'data-prompt',
        'data-genesis-search',
        'data-genesis-record',
        'data-genesis-count',
      ],
      FORBID_TAGS: [
        'script',
        'iframe',
        'object',
        'embed',
        'form',
        'link',
        'meta',
        'base',
        'textarea',
        'select',
      ],
      FORBID_ATTR: ['srcdoc', 'nonce', 'target'],
    });
    return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'nonce-${nonce}'; style-src 'unsafe-inline'; img-src data:; connect-src 'none'; font-src 'none'; form-action 'none'; base-uri 'none'"><style>body{margin:0;color:#253e34;background:#fbfaf6;font:16px/1.6 system-ui,sans-serif;overflow-wrap:anywhere}*{box-sizing:border-box}img,svg{max-width:100%}button,a{touch-action:manipulation}:focus-visible{outline:3px solid #c75a35;outline-offset:3px}pre{overflow:auto}table{max-width:100%}</style></head><body>${safe}<script nonce="${nonce}">
      const report = () => parent.postMessage({type:'genesis:height',height:document.documentElement.scrollHeight},'*');
      (${installGenesisInteractions.toString()})(document);
      new ResizeObserver(report).observe(document.body); report();
      document.addEventListener('click', event => {
        if (!event.isTrusted) return;
        const action = event.target.closest('[data-action="genesis_prompt"]');
        if (action) { event.preventDefault(); const prompt=action.dataset.prompt; if(prompt) parent.postMessage({type:'genesis:prompt',prompt},'*'); }
        const link = event.target.closest('a');
        if(link) { event.preventDefault(); parent.postMessage({type:'genesis:link',href:link.getAttribute('href')},'*'); }
      });
    </script></body></html>`;
  }, [html, nonce]);
  useEffect(() => {
    const receive = (event) => {
      if (event.source !== frame.current?.contentWindow) return;
      const data = event.data || {};
      if (data.type === 'genesis:height' && Number.isFinite(data.height))
        setHeight(Math.max(80, Math.min(2400, data.height)));
      if (
        data.type === 'genesis:prompt' &&
        typeof data.prompt === 'string' &&
        data.prompt.length <= 8000
      )
        onGenesisAction?.({ prompt: data.prompt });
      if (
        data.type === 'genesis:link' &&
        typeof data.href === 'string' &&
        /^https?:\/\//i.test(data.href)
      )
        window.open(data.href, '_blank', 'noopener,noreferrer');
    };
    window.addEventListener('message', receive);
    return () => window.removeEventListener('message', receive);
  }, [onGenesisAction]);
  return (
    <iframe
      ref={frame}
      title="LifeBot response"
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      srcDoc={documentHtml}
      style={{ width: '100%', height, border: 0, display: 'block' }}
    />
  );
}
